import { BoardColumns, BoardData, boardEntityName, BoardFile } from '@/backend/features/board-files/shared';
import { useTranslation } from '@/ui/hooks';
import { HtmlOutput } from '@juvare/common-form-ui/HtmlOutput';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { useAttachmentClient, useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import { getAttachmentUrl } from '@juvare/platform-sdk-react/ui/utils';
import { Row } from '@juvare/platform-shared/features/storage';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Box, Button, IconButton, Link, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHandleEditBoard } from '../hooks/useHandleEditBoard';
import { useHandleUploadFiles } from '../hooks/useHandleUploadFiles';

export interface BoardFilesListProps {
    row: Row<BoardColumns>;
    reload: () => void;
}

export const BoardFilesList: React.FC<BoardFilesListProps> = ({ row, reload }) => {
    const { t } = useTranslation();
    const { permissions } = useSelector(getUserState);
    const allowUpload = permissions.includes('upload');
    const handleUploadFiles = useHandleUploadFiles(reload);
    const handleEditBoard = useHandleEditBoard(row, reload);

    const filesCount = Math.min(row.columns.attachmentKeys.length, row.columns.fileNames.length);
    const files: BoardFile[] = [];
    for (let i = 0; i < filesCount; i++) {
        files.push({ attachmentKey: row.columns.attachmentKeys[i], fileName: row.columns.fileNames[i] });
    }

    const boardStorage = useStorageClient<BoardData, BoardColumns>(boardEntityName);
    const attachmentClient = useAttachmentClient();

    if (allowUpload) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {files.map((file) => (
                        <Box key={file.attachmentKey} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Link
                                href={getAttachmentUrl({
                                    entity: boardEntityName,
                                    entityId: row.id,
                                    key: file.attachmentKey,
                                    downloadFileName: file.fileName,
                                })}
                            >
                                {file.fileName}
                            </Link>
                            <Tooltip title={t('delete')}>
                                <IconButton
                                    size='small'
                                    color='primary'
                                    onClick={() => {
                                        showConfirmDialog({
                                            title: t('deleteFile', { name: file.fileName }),
                                            confirmLabel: t('delete'),
                                            onConfirm: () => {
                                                boardStorage
                                                    .post({
                                                        action: 'partial-update',
                                                        id: row.id,
                                                        data: {
                                                            files: files.filter(
                                                                ({ attachmentKey }) => attachmentKey !== file.attachmentKey,
                                                            ),
                                                        },
                                                    })
                                                    .then(() =>
                                                        attachmentClient.delete({
                                                            entity: boardEntityName,
                                                            entityId: row.id,
                                                            key: file.attachmentKey,
                                                        }),
                                                    )
                                                    .then(() => reload())
                                                    .catch(handleErrorDialog);
                                            },
                                        });
                                    }}
                                >
                                    <DeleteOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                    {allowUpload && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant='outlined' size='small' onClick={() => handleUploadFiles(row)}>
                                {t('uploadFiles')}
                            </Button>
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {row.columns.descriptionHtml ? (
                        <Box sx={{ '& p': { typography: 'body2' } }}>
                            <HtmlOutput html={row.columns.descriptionHtml} />
                        </Box>
                    ) : (
                        <Typography variant='body2' sx={{ color: themeColors.text.subtle }}>
                            <em>{t('descriptionIsNotSet')}</em>
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant='outlined' size='small' onClick={handleEditBoard}>
                            {t('edit')}
                        </Button>
                        <Button
                            variant='outlined'
                            size='small'
                            onClick={() => {
                                showConfirmDialog({
                                    title: t('deleteBoard', { name: row.columns.board }),
                                    confirmLabel: t('delete'),
                                    onConfirm: () => {
                                        boardStorage
                                            .post({ action: 'delete', id: row.id })
                                            .then(() => reload())
                                            .catch(handleErrorDialog);
                                    },
                                });
                            }}
                        >
                            {t('delete')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    } else {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {files.map((file) => (
                        <Box key={file.attachmentKey}>
                            <Link
                                href={getAttachmentUrl({
                                    entity: boardEntityName,
                                    entityId: row.id,
                                    key: file.attachmentKey,
                                    downloadFileName: file.fileName,
                                })}
                            >
                                {file.fileName}
                            </Link>
                        </Box>
                    ))}
                </Box>
                {row.columns.descriptionHtml && (
                    <Box sx={{ '& p': { typography: 'body2' } }}>
                        <HtmlOutput html={row.columns.descriptionHtml} />
                    </Box>
                )}
            </Box>
        );
    }
};
